/**
 * Tranche K — remote control of the live app.
 *
 * The shared vocabulary for `platform_config`: what each well-known config key
 * holds, how to read one safely, and how a client version compares against a
 * floor. Framework-free, so the api, the web app and the mobile app
 * all speak the same shapes instead of three near-copies drifting apart.
 *
 * Plan: `docs/superpowers/plans/admin-console/K-remote-control.md`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ EVERY PARSER HERE IS TOTAL, AND FAILS OPEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Not one function in this file throws, and every one of them treats "I could
 * not read that" as **the permissive answer**: maintenance off, feature on, no
 * version floor.
 *
 * This is the single most important property in the tranche. A config service
 * that fails CLOSED can brick every installed app from one bad deploy, one
 * corrupt jsonb value or one Redis blip — strictly worse than the problem
 * remote control exists to solve. A row nobody has written yet, a value of the
 * wrong type, a `null`, a string where an object belongs: all of them mean
 * "no opinion", never "shut it down".
 *
 * So there is no `parseOrThrow` variant, and adding one would defeat the point.
 */

import { FEATURES, type Feature } from './entitlements.js';

// ---------------------------------------------------------------------------
// The well-known keys
// ---------------------------------------------------------------------------

/**
 * Every key `platform_config` may hold. The table is key/value, but the set of
 * keys is CLOSED and lives here — a typo'd key written by hand would otherwise
 * be a row that silently does nothing forever.
 *
 * ⚠️ ADDING A KEY COSTS A MIGRATION. The table carries a
 * `platform_config_key_known` CHECK listing these strings verbatim
 * (`20260825200000_platform_config`), so a key added here and not there is
 * rejected by Postgres at write time. `storeInstalls` was added on 2026-08-25
 * by `..._platform_config_store_installs`; copy that migration's shape.
 */
export const PLATFORM_CONFIG_KEYS = [
  'maintenance',
  'featureFlags',
  'minAppVersion',
  'storeInstalls',
] as const;
export type PlatformConfigKey = (typeof PLATFORM_CONFIG_KEYS)[number];

export function isPlatformConfigKey(raw: unknown): raw is PlatformConfigKey {
  return typeof raw === 'string' && (PLATFORM_CONFIG_KEYS as readonly string[]).includes(raw);
}

// ---------------------------------------------------------------------------
// maintenance
// ---------------------------------------------------------------------------

export interface MaintenanceState {
  active: boolean;
  /** Hebrew text shown on the "back shortly" screen. Null → the client's own default copy. */
  message: string | null;
}

/**
 * Cap on the admin-written maintenance message.
 *
 * This string is served from an UNAUTHENTICATED endpoint to every app launch,
 * so it is both a payload-size question and a defacement surface. 280
 * characters is more than enough for "we are back at 14:00" and small enough
 * that it can never dominate the response.
 */
export const MAINTENANCE_MESSAGE_MAX_LENGTH = 280;

export const MAINTENANCE_OFF: MaintenanceState = { active: false, message: null };

/** Total. Anything that is not explicitly `{active: true}` reads as OFF. */
export function parseMaintenance(raw: unknown): MaintenanceState {
  if (typeof raw !== 'object' || raw === null) return MAINTENANCE_OFF;
  const value = raw as { active?: unknown; message?: unknown };
  // Strict `=== true`, not truthiness: the string "false" is truthy, and a
  // hand-edited jsonb value is exactly where that would come from.
  if (value.active !== true) return MAINTENANCE_OFF;
  const message =
    typeof value.message === 'string' && value.message.trim() !== ''
      ? value.message.slice(0, MAINTENANCE_MESSAGE_MAX_LENGTH)
      : null;
  return { active: true, message };
}

// ---------------------------------------------------------------------------
// featureFlags
// ---------------------------------------------------------------------------

/**
 * One feature's kill-switch state.
 *
 * `enabled: false` turns it off for everyone. `disabledForBusinessIds` turns
 * it off for named businesses only — the explicit-list targeting the plan kept
 * after cutting percentage rollout, because with 47 businesses a list does the
 * same job and can be reasoned about by reading it.
 */
export interface FeatureFlagState {
  enabled: boolean;
  disabledForBusinessIds: string[];
}

export const FEATURE_ON: FeatureFlagState = { enabled: true, disabledForBusinessIds: [] };

/** Sparse by design: an absent key means "on", so the common state stores nothing. */
export type FeatureFlagsState = Partial<Record<Feature, FeatureFlagState>>;

/** How many businesses one flag may name. A longer list means it should be global. */
export const FEATURE_TARGET_MAX_BUSINESSES = 100;

function parseFeatureFlagState(raw: unknown): FeatureFlagState {
  if (typeof raw !== 'object' || raw === null) return FEATURE_ON;
  const value = raw as { enabled?: unknown; disabledForBusinessIds?: unknown };
  // Only an explicit `false` disables. Absent, null, "false", 0 → on.
  const enabled = value.enabled !== false;
  const ids = Array.isArray(value.disabledForBusinessIds)
    ? value.disabledForBusinessIds
        .filter((id): id is string => typeof id === 'string' && id !== '')
        .slice(0, FEATURE_TARGET_MAX_BUSINESSES)
    : [];
  return { enabled, disabledForBusinessIds: ids };
}

/**
 * Total. Keys outside the shared `FEATURES` registry are DROPPED, not kept:
 * the registry is the only vocabulary the clients understand, and echoing an
 * unknown key back out of the public endpoint would leak the name of whatever
 * someone was mid-way through building.
 */
export function parseFeatureFlags(raw: unknown): FeatureFlagsState {
  if (typeof raw !== 'object' || raw === null) return {};
  const source = raw as Record<string, unknown>;
  const out: FeatureFlagsState = {};
  for (const feature of FEATURES) {
    if (!(feature in source)) continue;
    const state = parseFeatureFlagState(source[feature]);
    // Store only the non-default states, so the shape stays sparse whichever
    // way it was written.
    if (!state.enabled || state.disabledForBusinessIds.length > 0) out[feature] = state;
  }
  return out;
}

/**
 * Is `feature` live for this caller? `businessId` is optional — the public
 * endpoint has none, and per-business targeting is enforced server-side where
 * one exists.
 */
export function isFeatureLive(
  flags: FeatureFlagsState,
  feature: Feature,
  businessId?: string | null,
): boolean {
  const state = flags[feature];
  if (!state) return true;
  if (!state.enabled) return false;
  if (businessId && state.disabledForBusinessIds.includes(businessId)) return false;
  return true;
}

/** The globally-off features, in stable registry order. What the public endpoint may reveal. */
export function globallyDisabledFeatures(flags: FeatureFlagsState): Feature[] {
  return FEATURES.filter((f) => flags[f]?.enabled === false);
}

// ---------------------------------------------------------------------------
// minAppVersion
// ---------------------------------------------------------------------------

/**
 * Platforms that CAN have a version floor.
 *
 * ⚠️ `web` is deliberately absent, and this is not an oversight. `X-App-Version`
 * carries `"0.1.0"` from mobile and a short git SHA like `bc07ab0` from web
 * (see `apps/api/src/trpc/client-version.ts`, which accepts both on purpose).
 * A SHA has no order, so any floor compared against one would be permanently
 * satisfied or permanently violated — and "permanently violated" means every
 * web visitor sees an update screen for a page that updates itself on reload.
 * The web app also cannot be out of date: the browser fetches the current
 * build every time.
 */
export const FLOORED_PLATFORMS = ['ios', 'android'] as const;
export type FlooredPlatform = (typeof FLOORED_PLATFORMS)[number];

export function isFlooredPlatform(raw: unknown): raw is FlooredPlatform {
  return raw === 'ios' || raw === 'android';
}

export interface VersionFloor {
  /** Below this → a dismissible nudge. */
  recommended: string | null;
  /** Below this → a blocking screen. */
  required: string | null;
}

export type MinAppVersionState = Record<FlooredPlatform, VersionFloor>;

export const NO_FLOOR: VersionFloor = { recommended: null, required: null };
export const NO_VERSION_FLOORS: MinAppVersionState = { ios: NO_FLOOR, android: NO_FLOOR };

/** How many dot-separated parts a version may carry. `1.2.3.4` is fine; more is nonsense. */
const MAX_VERSION_PARTS = 4;

/**
 * `"1.12.3"` → `[1, 12, 3]`. Returns null for ANYTHING that is not a plain
 * dotted run of digits — a git SHA, a build tag, `"1.2.3-beta"`, `""`.
 *
 * Strict on purpose. A lenient parser that read `"bc07ab0"` as `[0]` would
 * place every web client below every floor.
 */
export function parseVersionParts(raw: unknown): number[] | null {
  if (typeof raw !== 'string' || raw === '') return null;
  const parts = raw.split('.');
  if (parts.length === 0 || parts.length > MAX_VERSION_PARTS) return null;
  const out: number[] = [];
  for (const part of parts) {
    if (!/^\d{1,6}$/.test(part)) return null;
    out.push(Number(part));
  }
  return out;
}

/**
 * Compares two dotted versions. Returns a negative number when `a` is older,
 * 0 when equal, positive when newer — and **null when either side cannot be
 * parsed**, which callers must read as "no opinion", never as "older".
 *
 * A shorter version is padded with zeros, so `1.2` === `1.2.0`.
 */
export function compareVersions(a: unknown, b: unknown): number | null {
  const left = parseVersionParts(a);
  const right = parseVersionParts(b);
  if (left === null || right === null) return null;
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function parseFloor(raw: unknown): VersionFloor {
  if (typeof raw !== 'object' || raw === null) return NO_FLOOR;
  const value = raw as { recommended?: unknown; required?: unknown };
  // A floor that does not parse as a version is stored as null — i.e. no
  // floor — rather than kept as an uncomparable string.
  return {
    recommended:
      parseVersionParts(value.recommended) === null ? null : (value.recommended as string),
    required: parseVersionParts(value.required) === null ? null : (value.required as string),
  };
}

/** Total. Unknown platforms are dropped; `web` can never gain a floor. */
export function parseMinAppVersion(raw: unknown): MinAppVersionState {
  if (typeof raw !== 'object' || raw === null) return NO_VERSION_FLOORS;
  const source = raw as Record<string, unknown>;
  return {
    ios: parseFloor(source.ios),
    android: parseFloor(source.android),
  };
}

export type UpdateRequirement = 'none' | 'recommended' | 'required';

/**
 * What this client should be told about updating.
 *
 * Returns `'none'` — the permissive answer — for every uncertainty:
 *  - the platform has no floor, or is `web`, or is unknown/absent;
 *  - the client sent no version, or sent one that does not parse (a SHA);
 *  - the configured floor does not parse.
 *
 * `'required'` wins over `'recommended'` when both floors are crossed.
 */
export function versionRequirement(args: {
  version: unknown;
  platform: unknown;
  floors: MinAppVersionState;
}): UpdateRequirement {
  if (!isFlooredPlatform(args.platform)) return 'none';
  const floor = args.floors[args.platform];
  const requiredCmp = compareVersions(args.version, floor.required);
  if (requiredCmp !== null && requiredCmp < 0) return 'required';
  const recommendedCmp = compareVersions(args.version, floor.recommended);
  if (recommendedCmp !== null && recommendedCmp < 0) return 'recommended';
  return 'none';
}

// ---------------------------------------------------------------------------
// storeInstalls
// ---------------------------------------------------------------------------

/**
 * The ONE honest install number in the whole product, and the only value in
 * this file a human types by hand.
 *
 * ⚠️ READ THE HONEST-METRICS DOCTRINE FIRST — `apps/api/src/trpc/admin.router.ts`
 * and the header of `apps/api/src/trpc/admin-system.router.ts`. Real installs
 * exist ONLY in App Store Connect and Google Play. Every number the database
 * can produce (`app_user` rows, `device_push_token` rows, version rows) is a
 * proxy that is wrong in a different direction, which is why none of them may
 * ever be labelled an install. This key holds the real figure precisely so the
 * proxies never have to pretend to be it.
 *
 * It is deliberately NOT part of `PublicPlatformConfig`: it is a business
 * number for two admins, not something an app launch needs, and the public
 * config endpoint is unauthenticated.
 *
 * ⚠️ It is a SNAPSHOT of one month that somebody typed, not a live counter. It
 * goes stale the moment the month ends and nobody retypes it, so `month` is
 * mandatory for the value to count as configured — a number with no month
 * attached is a number nobody can date, and an undated install count is worse
 * than none.
 */
export interface StoreInstallsState {
  /** 'YYYY-MM' the figure refers to. Null → nothing has been typed. */
  month: string | null;
  /** App Store Connect installs for `month`. Null → not reported. */
  ios: number | null;
  /** Google Play installs for `month`. Null → not reported. */
  android: number | null;
}

/** Nothing typed yet. The shipped state, and every parse failure. */
export const NO_STORE_INSTALLS: StoreInstallsState = { month: null, ios: null, android: null };

/**
 * Upper bound on a hand-typed count.
 *
 * Not a business assumption about how big the app can get — it is a typo
 * guard. A fat-fingered extra digit on a stealth product with tens of installs
 * would otherwise be rendered as fact on the console for a month.
 */
export const STORE_INSTALLS_MAX = 100_000_000;

/** `'2026-08'` — a real calendar month, never `'2026-13'`. */
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isStoreInstallMonth(raw: unknown): raw is string {
  return typeof raw === 'string' && MONTH_RE.test(raw);
}

/** A count, or null. Rejects negatives, fractions, NaN and anything oversized. */
function parseInstallCount(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isInteger(raw)) return null;
  if (raw < 0 || raw > STORE_INSTALLS_MAX) return null;
  return raw;
}

/**
 * Total, like every parser here. A blob of the wrong shape reads as "nothing
 * was typed", never as a zero — the doctrine's rule that an unmeasurable thing
 * must report NOT-CHECKED rather than a green zero applies to a hand-typed
 * number too. "0 installs" and "nobody has filled this in" are different
 * facts, and only one of them is ever true here.
 */
export function parseStoreInstalls(raw: unknown): StoreInstallsState {
  if (typeof raw !== 'object' || raw === null) return NO_STORE_INSTALLS;
  const value = raw as { month?: unknown; ios?: unknown; android?: unknown };
  const month = isStoreInstallMonth(value.month) ? value.month : null;
  // No month means no dateable figure, so the counts are dropped with it
  // rather than shown floating free.
  if (month === null) return NO_STORE_INSTALLS;
  return { month, ios: parseInstallCount(value.ios), android: parseInstallCount(value.android) };
}

/**
 * Is there anything real to show?
 *
 * A month alone is NOT configured: somebody who saved a month and no numbers
 * has told us nothing. At least one platform figure has to be present.
 */
export function isStoreInstallsConfigured(state: StoreInstallsState): boolean {
  return state.month !== null && (state.ios !== null || state.android !== null);
}

// ---------------------------------------------------------------------------
// The public payload
// ---------------------------------------------------------------------------

/**
 * What `config.get` returns to an UNAUTHENTICATED caller.
 *
 * Deliberately smaller than the config table. It carries only what a client
 * must have in order to render something:
 *
 *  - `disabledFeatures` is restricted to the shared `FEATURES` registry, which
 *    already ships inside every client bundle, so it reveals nothing new. An
 *    internal flag key would leak what is being built — this is a stealth
 *    company — so `parseFeatureFlags` drops unknown keys before they get here.
 *  - Per-business targeting is NOT included. The endpoint has no business
 *    context, and the server gate enforces it anyway.
 *  - `updatedByUserId`, `updatedAt` and every other key stay server-side.
 */
export interface PublicPlatformConfig {
  maintenance: MaintenanceState;
  disabledFeatures: Feature[];
  minAppVersion: MinAppVersionState;
}

/**
 * The fail-open default: everything on, nothing blocked.
 *
 * This is what a client uses when `config.get` errors, times out, or has never
 * been reached — and what the server returns when it cannot read the table.
 * Frozen so a caller that mutates its result cannot poison the next one.
 */
export const OPEN_PLATFORM_CONFIG: PublicPlatformConfig = Object.freeze({
  maintenance: MAINTENANCE_OFF,
  disabledFeatures: [] as Feature[],
  minAppVersion: NO_VERSION_FLOORS,
});
